import { useState, useCallback, useRef, useEffect } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { requestLiveKitConnect, reportAvatarSessionEnd } from '../api/publicProposals';

/**
 * useLiveKitAvatar
 * ================
 * Manages a LiveKit room connection for avatar-driven proposal presentation.
 * Handles track subscriptions, data messaging for control, and slide advance callbacks.
 */
const useLiveKitAvatar = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [videoTrack, setVideoTrack] = useState(null);
  const [audioTrack, setAudioTrack] = useState(null);
  const [agentState, setAgentState] = useState('idle');
  const [agentActivity, setAgentActivity] = useState('idle'); // idle | listening | thinking | speaking
  const [slidesLocked, setSlidesLocked] = useState(false);
  const [avatarName, setAvatarName] = useState(null);

  const roomRef = useRef(null);
  const onSlideAdvanceRef = useRef(null);
  const sessionStartRef = useRef(null);
  const proposalTokenRef = useRef(null);
  const agentDidPresentRef = useRef(false);
  const lastDisconnectRef = useRef(0);

  const setOnSlideAdvance = useCallback((cb) => {
    onSlideAdvanceRef.current = cb;
  }, []);

  /** Send a JSON data message to the room */
  const sendMessage = useCallback((msg) => {
    const room = roomRef.current;
    if (!room || !room.localParticipant) return;
    const payload = new TextEncoder().encode(JSON.stringify(msg));
    room.localParticipant.publishData(payload, { reliable: true });
  }, []);

  /** Handle incoming data messages from agent */
  const handleDataReceived = useCallback((data) => {
    try {
      const msg = JSON.parse(new TextDecoder().decode(data));
      if (msg.type === 'advance_slide' && typeof msg.section === 'number') {
        if (onSlideAdvanceRef.current) {
          onSlideAdvanceRef.current(msg.section);
        }
      }
      // Update agent state from server-side messages
      if (msg.type === 'state_change' && msg.state) {
        setAgentState(msg.state);
      }
      // Lock/unlock slide navigation during presentation
      if (msg.type === 'slides_locked') {
        setSlidesLocked(!!msg.locked);
      }
    } catch {
      // ignore non-JSON data
    }
  }, []);

  /** Track agent speaking via ActiveSpeakersChanged */
  const handleActiveSpeakersChanged = useCallback((speakers) => {
    const agentSpeaking = speakers.some(p => {
      const identity = (p.identity || '').toLowerCase();
      const attrs = p.attributes || {};
      return identity.includes('agent') || attrs['lk.agent.state'] || p.kind === 'agent';
    });
    if (agentSpeaking) {
      setAgentActivity('speaking');
    } else {
      // When agent stops speaking, switch to listening (if in QA mode)
      setAgentActivity(prev => prev === 'speaking' ? 'listening' : prev);
    }
  }, []);

  /** Track agent state from participant attributes (thinking/listening) */
  const handleParticipantAttributesChanged = useCallback((changedAttrs, participant) => {
    if (!participant) return;
    const identity = (participant.identity || '').toLowerCase();
    const attrs = participant.attributes || {};
    const isAgent = identity.includes('agent') || attrs['lk.agent.state'] || participant.kind === 'agent';
    if (!isAgent) return;

    const lkState = changedAttrs?.['lk.agent.state'] || attrs['lk.agent.state'];
    if (lkState) {
      // Map LiveKit agent states: initializing, listening, thinking, speaking
      setAgentActivity(lkState);
    }
  }, []);

  /** Check if a participant is the avatar agent */
  const isAvatarParticipant = useCallback((participant) => {
    if (!participant) return false;
    const identity = (participant.identity || '').toLowerCase();
    if (identity.includes('avatar') || identity.includes('anam')) return true;
    // Check for LiveKit agent attribute
    const attrs = participant.attributes || {};
    if (attrs['lk.publish_on_behalf'] || participant.kind === 'agent') return true;
    return false;
  }, []);

  /** Handle track subscription — accept all remote tracks (agent + avatar) */
  const handleTrackSubscribed = useCallback((track, publication, participant) => {
    if (track.kind === 'video') {
      setVideoTrack(track);
      agentDidPresentRef.current = true;
    } else if (track.kind === 'audio') {
      setAudioTrack(track);
    }
  }, []);

  /** Handle track unsubscription */
  const handleTrackUnsubscribed = useCallback((track) => {
    if (track.kind === 'video') {
      setVideoTrack((prev) => (prev === track ? null : prev));
    } else if (track.kind === 'audio') {
      setAudioTrack((prev) => (prev === track ? null : prev));
    }
  }, []);

  /** Report session end for cost tracking — only if the agent actually presented */
  const endSession = useCallback(async () => {
    if (sessionStartRef.current && proposalTokenRef.current && agentDidPresentRef.current) {
      try {
        await reportAvatarSessionEnd(proposalTokenRef.current, sessionStartRef.current);
      } catch (err) {
        console.warn('[useLiveKitAvatar] Failed to report session end:', err);
      }
      sessionStartRef.current = null;
      agentDidPresentRef.current = false;
    }
  }, []);

  /** Connect to the LiveKit room */
  const connect = useCallback(async (proposalToken) => {
    if (roomRef.current || isConnecting) return; // already connected or connecting
    // Cooldown: wait at least 5s after last disconnect to avoid concurrent sessions
    const timeSinceDisconnect = Date.now() - lastDisconnectRef.current;
    if (lastDisconnectRef.current > 0 && timeSinceDisconnect < 5000) {
      const waitMs = 5000 - timeSinceDisconnect;
      setIsConnecting(true);
      setAgentState('idle');
      await new Promise(resolve => setTimeout(resolve, waitMs));
    }
    setIsConnecting(true);
    setError(null);
    setAgentState('idle');
    agentDidPresentRef.current = false;

    try {
      const res = await requestLiveKitConnect(proposalToken);
      const inner = res.data || res;
      sessionStartRef.current = inner.session_started_at;
      proposalTokenRef.current = proposalToken;
      if (inner.avatar_name) setAvatarName(inner.avatar_name);
      const livekit_url = inner.server_url;
      const livekit_token = inner.participant_token;

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.on(RoomEvent.DataReceived, handleDataReceived);
      room.on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
      room.on(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged);
      room.on(RoomEvent.Disconnected, () => {
        lastDisconnectRef.current = Date.now();
        endSession();
        setIsConnected(false);
        setAgentState(prev => prev === 'ended' ? 'ended' : 'idle');
        setAgentActivity('idle');
        setSlidesLocked(false);
        setVideoTrack(null);
        setAudioTrack(null);
        roomRef.current = null;
      });
      // Detect avatar/agent leaving (session limit, crash, etc.)
      room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        if (isAvatarParticipant(participant)) {
          endSession();
          setAgentState('ended');
          setVideoTrack(null);
          setAudioTrack(null);
        }
      });

      await room.connect(livekit_url, livekit_token);
      roomRef.current = room;

      // Enable microphone so the agent can hear the client
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (micErr) {
        console.warn('[useLiveKitAvatar] Could not enable mic:', micErr);
      }

      setIsConnected(true);
      setAgentState('presenting');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to connect to presentation');
      setAgentState('idle');
    } finally {
      setIsConnecting(false);
    }
  }, [handleTrackSubscribed, handleTrackUnsubscribed, handleDataReceived, handleActiveSpeakersChanged, handleParticipantAttributesChanged, endSession]);

  /** Disconnect from the LiveKit room */
  const disconnect = useCallback(async () => {
    await endSession();
    const room = roomRef.current;
    if (room) {
      room.disconnect();
      roomRef.current = null;
    }
    lastDisconnectRef.current = Date.now();
    setIsConnected(false);
    setIsConnecting(false);
    setVideoTrack(null);
    setAudioTrack(null);
    setAgentState('idle');
    setError(null);
  }, [endSession]);

  /** Restart presentation — disconnect current session, wait for cleanup, then reconnect */
  const restart = useCallback(async () => {
    const token = proposalTokenRef.current;
    await endSession();
    const room = roomRef.current;
    if (room) {
      room.disconnect();
      roomRef.current = null;
    }
    setIsConnected(false);
    setVideoTrack(null);
    setAudioTrack(null);
    setAgentState('idle');
    setAgentActivity('idle');
    setError(null);
    agentDidPresentRef.current = false;
    // Wait for old session to fully clean up before starting new one
    await new Promise(resolve => setTimeout(resolve, 5000));
    if (token) {
      await connect(token);
    }
  }, [connect, endSession]);

  /** Raise hand — pause presentation and switch to Q&A mode after current slide */
  const raiseHand = useCallback(() => {
    sendMessage({ type: 'raise_hand' });
    setAgentState('qa_pending');
  }, [sendMessage]);

  /** Resume — switch back to presentation mode */
  const resumePresentation = useCallback(() => {
    sendMessage({ type: 'resume' });
    setAgentState('presenting');
  }, [sendMessage]);

  /** Navigate to a specific section */
  const goToSection = useCallback((index) => {
    sendMessage({ type: 'slide_change', section: index });
  }, [sendMessage]);

  /** Cleanup on unmount */
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on page close — only if agent presented
      if (sessionStartRef.current && proposalTokenRef.current && agentDidPresentRef.current) {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const url = `${API_BASE_URL}/api/v1/public/proposals/${proposalTokenRef.current}/avatar/session-end`;
        const body = JSON.stringify({ session_started_at: sessionStartRef.current });
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        sessionStartRef.current = null;
      }
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    };
  }, []);

  return {
    isConnecting,
    isConnected,
    error,
    videoTrack,
    audioTrack,
    agentState,
    agentActivity,
    slidesLocked,
    avatarName,
    connect,
    disconnect,
    restart,
    raiseHand,
    resumePresentation,
    goToSection,
    setOnSlideAdvance,
  };
};

export default useLiveKitAvatar;
